// src/services/serverClock.js
// Client-side handshake with the authoritative server clock (/api/time).
// See docs/decisions/adr-class-visibility-and-premiere-sync.md (Feature B).
//
// Model: fetch /api/time a few times, estimate the server time at the moment each
// response lands (correcting for round-trip latency), and keep the OFFSET between
// server time and this device's local clock. Thereafter `serverNow()` returns
// `Date.now() + offset` — using the device's monotonic local progression but
// anchored to the server, so the device's (possibly wrong) absolute clock value
// no longer matters.
//
// GRACEFUL DEGRADATION — the load-bearing safety property:
//   Until a sync succeeds, `offset` is 0, so `serverNow() === Date.now()` — i.e.
//   EXACTLY today's behavior. If every sample fails, it stays that way. Premiere
//   sync can therefore never be *worse* than the pre-feature local-clock code.

let clockOffsetMs = 0;
let synced = false;
let inFlight = null;

// serverNow(): best available estimate of server epoch-ms. Falls back to the
// local clock (offset 0) when unsynced. Never throws.
export function serverNow() {
    return Date.now() + clockOffsetMs;
}

export function isServerClockSynced() {
    return synced;
}

export function getClockOffsetMs() {
    return clockOffsetMs;
}

async function sampleOnce() {
    const t0 = Date.now();
    const res = await fetch('/api/time', { cache: 'no-store' });
    if (!res.ok) throw new Error(`time HTTP ${res.status}`);
    const data = await res.json();
    const t1 = Date.now();
    const serverTime = Number(data && data.now);
    if (!Number.isFinite(serverTime)) throw new Error('time payload invalid');
    const rtt = t1 - t0;
    // The server captured `serverTime` ~rtt/2 ago; estimate its clock at t1.
    const estimatedServerAtT1 = serverTime + rtt / 2;
    return { offset: estimatedServerAtT1 - t1, rtt };
}

// syncServerClock(): sample a few times and keep the lowest-latency estimate
// (NTP-style — the fastest round-trip has the least one-way uncertainty).
// Coalesced: concurrent callers share one in-flight handshake. Idempotent and
// cheap to call from multiple mount points; safe to await or fire-and-forget.
export async function syncServerClock({ samples = 3 } = {}) {
    if (inFlight) return inFlight;
    inFlight = (async () => {
        const results = [];
        for (let i = 0; i < samples; i += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                results.push(await sampleOnce());
            } catch (_) { /* skip this sample; degrade gracefully */ }
        }
        if (results.length > 0) {
            results.sort((a, b) => a.rtt - b.rtt);
            clockOffsetMs = results[0].offset;
            synced = true;
        }
        inFlight = null;
        return clockOffsetMs;
    })();
    return inFlight;
}
